import { createSelector } from 'reselect';

import { CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID } from '../../constants/bucket';
import { CONTENT_TYPE_LABEL } from '../../constants/createSend';
import { IOrgAddress } from '../../types/addressBook';
import { TCreateSendBucketItem } from '../../types/bucket';
import { ICampaign, ICampaignItem } from '../../types/campaigns';
import { ICreateSendCrmLink } from '../../types/createSendCrm';
import { ICreateSendRecipient, ShippingModeEnum, TEffectiveShippingMode } from '../../types/createSendRecipients';
import { IOnDemandItem } from '../../types/inventories';
import { IGiftBudget } from '../../types/marketplace';
import { ISendsV2Recipient, ISendsV2Request, ISendsV2ShippingFields } from '../../types/sendsV2';
import { ShippingOptionsEnum } from '../../types/shipping';
import { normalizeStateCode } from '../../utils/addressValidation';
import { mapLinkFixedAddress } from '../../utils/createSendLink';
import { mapItemsToRequest } from '../../utils/engagementRequest';
import {
  buildMarketplaceRequestMap,
  resolveLooseContentsEngagementId,
  splitBucketBySource,
} from '../../utils/marketplacePayload';
import { orderPygOptionIds } from '../../utils/pygOptions';
import {
  selectCsBoxId,
  selectCsGiftBudget,
  selectCsItems,
  selectCsKits,
  selectCsMarketplacePygPicks,
  selectCsPygOptionOrder,
} from '../createSend/selectors/contents';
import { selectCsCrmByRecipientUid } from '../createSend/selectors/crm';
import {
  selectCompleteRecipients,
  selectCsClaimAddressIds,
  selectCsIsGroupShipmentEnabled,
  selectCsOverrideAddress,
  selectEffectiveShipping,
} from '../createSend/selectors/recipients';
import {
  selectCsDisableSenderEmails,
  selectCsInternalNotes,
  selectCsShippingOption,
} from '../createSend/selectors/sendSettings';
import {
  selectEffectiveCsClaimPage,
  selectEffectiveCsCustomMessage,
  selectEffectiveCsEmail,
  selectEffectiveCsReminderInterval,
  selectEffectiveCsSendSettings,
  selectIsCreateSendContentDigital,
} from './createSendFlow';
import { selectCurrentOrganizationCRMType } from './organizations';

interface ISendsV2Contents {
  pre_created_engagement_id: string;
  pick_campaign_ids: string[] | null;
  items: ISendsV2Request['items'];
  marketplace?: ISendsV2Request['marketplace'];
  gift_budget?: ISendsV2Request['gift_budget'];
}

/**
 * Contents for the sends-v2 request. Marketplace picks live in the `marketplace` map, never in
 * `items[]`; PYG picks also reference it from `pick_campaign_ids` by prefixed product id, and a
 * custom cart's absence from that list is what marks it as a cart rather than a set of options
 * (contract variants 4.1–4.8). Mirrors `mapLinkContents` on the one-link path.
 */
export const mapRecipientsContents = (
  kits: ICampaign[] | null,
  items: TCreateSendBucketItem[] | null,
  preCreatedBoxId: string | undefined,
  giftBudget: IGiftBudget | null = null,
  pygOptionOrder: string[] = [],
): ISendsV2Contents => {
  const selectedKits = kits ?? [];
  const { ieItems, marketplaceItems } = splitBucketBySource<ICampaignItem | IOnDemandItem>(items);
  const isCustomCart = preCreatedBoxId === CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID;
  const pygPicks = isCustomCart ? [] : marketplaceItems;
  const hasMarketplaceContext = marketplaceItems.length > 0 || giftBudget !== null;

  // The contract wants explicit nulls inside marketplace context, never missing keys.
  const marketplaceFields = hasMarketplaceContext
    ? { marketplace: buildMarketplaceRequestMap(marketplaceItems), gift_budget: giftBudget }
    : {};

  // One kit beside a PYG pick is a multi-option send: the single-kit branch would emit that
  // kit's items too.
  if (selectedKits.length === 1 && pygPicks.length === 0) {
    return {
      pre_created_engagement_id: selectedKits[0].box_id,
      pick_campaign_ids: hasMarketplaceContext ? [] : null,
      items: mapItemsToRequest(selectedKits[0].items ?? []),
      ...marketplaceFields,
    };
  }

  if (selectedKits.length + pygPicks.length > 1) {
    return {
      pre_created_engagement_id: '',
      pick_campaign_ids: orderPygOptionIds(selectedKits, pygPicks, pygOptionOrder),
      items: null,
      ...marketplaceFields,
    };
  }

  const requestItems = mapItemsToRequest(ieItems);

  return {
    pre_created_engagement_id: resolveLooseContentsEngagementId(preCreatedBoxId, ieItems.length, hasMarketplaceContext),
    // Only reached with at most one option, so nothing is referenced: a lone marketplace product
    // is variant 4.1, whose `pick_campaign_ids` is `[]` — referencing its id would present a
    // single-option PYG instead. Multi-option sends took the branch above.
    pick_campaign_ids: hasMarketplaceContext ? [] : null,
    // The contract wants an explicit `null`, not an empty array, when there are no IE items.
    items: hasMarketplaceContext && !requestItems.length ? null : requestItems,
    ...marketplaceFields,
  };
};

export const includesRecipientAddress = (mode: TEffectiveShippingMode, hasOverride: boolean): boolean =>
  (mode === ShippingModeEnum.YouProvide ||
    mode === ShippingModeEnum.FixedAddress ||
    mode === ShippingModeEnum.RecipientDecides) &&
  !hasOverride;

export const mapRecipientsShipping = (
  mode: TEffectiveShippingMode,
  isClaimBased: boolean,
  overrideAddress: IOrgAddress | null,
  claimAddressIds: string[],
  isGroupShipmentEnabled: boolean,
  shippingOption: ShippingOptionsEnum,
): Partial<ISendsV2ShippingFields> => {
  const base = {
    is_delayed_shipping: isClaimBased,
    is_office_dropship_enabled: isGroupShipmentEnabled,
    shipping_option: shippingOption,
  };

  if (mode === ShippingModeEnum.RecipientDecides || mode === ShippingModeEnum.FromList) {
    return {
      ...base,
      is_receiver_address_fixed: mode === ShippingModeEnum.FromList,
      ...(claimAddressIds.length ? { org_address_ids: claimAddressIds } : {}),
    };
  }

  const fixedAddress = mapLinkFixedAddress(overrideAddress);

  return {
    ...base,
    is_receiver_address_fixed: Boolean(overrideAddress) || mode === ShippingModeEnum.FixedAddress,
    ...(fixedAddress ? { receiver_fixed_address: fixedAddress } : {}),
  };
};

export const mapRecipientToSendsV2 = (
  recipient: ICreateSendRecipient,
  crmLink: ICreateSendCrmLink | undefined,
  crmType: string,
  includeAddress: boolean,
): ISendsV2Recipient => {
  const { address } = recipient;

  return {
    email: recipient.email.trim(),
    first_name: recipient.first_name.trim(),
    last_name: recipient.last_name.trim(),
    ...(recipient.phone ? { phone: recipient.phone } : {}),
    ...(includeAddress && address?.company_name ? { company_name: address.company_name } : {}),
    ...(includeAddress && address
      ? {
          address: {
            address1: address.address1,
            address2: address.address2 ?? '',
            city: address.city,
            state: normalizeStateCode(address.state, address.country),
            zip: address.zip,
            country: address.country,
          },
        }
      : {}),
    crm_type: crmType,
    crm_record_id: crmLink?.recordId ?? '',
    crm_record_type: crmLink?.recordType ?? '',
    crm_opportunity_id: crmLink?.opportunityId ?? '',
    ...(includeAddress && recipient.tax_number ? { tax_number: recipient.tax_number } : {}),
  };
};

export const selectCsBulkTitleCampaignName = createSelector(
  selectCsKits,
  selectCsMarketplacePygPicks,
  (kits, pygPicks): string => {
    const selectedKits = kits ?? [];

    // A kit with PYG picks beside it is a multi-option send, so its name would mislabel it.
    if (selectedKits.length === 1 && pygPicks.length === 0) {
      return selectedKits[0].name ?? '';
    }

    return selectedKits.length + pygPicks.length > 1 ? CONTENT_TYPE_LABEL.pickYourGift : CONTENT_TYPE_LABEL.customItems;
  },
);

export const selectCreateSendRecipientsCandidate = createSelector(
  selectCsKits,
  selectCsItems,
  selectCsBoxId,
  selectCsGiftBudget,
  selectIsCreateSendContentDigital,
  selectCompleteRecipients,
  selectCsCrmByRecipientUid,
  selectCurrentOrganizationCRMType,
  selectEffectiveShipping,
  selectCsOverrideAddress,
  selectCsClaimAddressIds,
  selectCsIsGroupShipmentEnabled,
  selectCsShippingOption,
  selectCsInternalNotes,
  selectCsDisableSenderEmails,
  selectEffectiveCsSendSettings,
  selectEffectiveCsEmail,
  selectEffectiveCsReminderInterval,
  selectEffectiveCsCustomMessage,
  selectEffectiveCsClaimPage,
  selectCsPygOptionOrder,
  (
    kits,
    items,
    preCreatedBoxId,
    giftBudget,
    isContentDigital,
    recipients,
    crmByRecipientUid,
    crmType,
    effectiveShipping,
    overrideAddress,
    claimAddressIds,
    isGroupShipmentEnabled,
    shippingOption,
    internalNotes,
    isSenderNotificationsDisabled,
    effectiveSettings,
    email,
    reminderInterval,
    customMessage,
    claimPage,
    pygOptionOrder,
  ): ISendsV2Request => {
    const includeAddress =
      !isContentDigital && includesRecipientAddress(effectiveShipping.mode, Boolean(overrideAddress));

    return {
      ...mapRecipientsContents(kits, items, preCreatedBoxId, giftBudget, pygOptionOrder),
      recipients: recipients.reduce<Record<string, ISendsV2Recipient>>(
        (acc, recipient) => ({
          ...acc,
          [recipient.uid]: mapRecipientToSendsV2(
            recipient,
            crmByRecipientUid[recipient.uid],
            crmType ?? '',
            includeAddress,
          ),
        }),
        {},
      ),
      business_reason_id: effectiveSettings.businessReasonId,
      internal_notes: internalNotes,
      note_message: customMessage.message,
      experience_id: claimPage.experienceId,
      ...(effectiveSettings.isOnHold && effectiveSettings.onHoldUntil
        ? { on_hold_until: effectiveSettings.onHoldUntil }
        : {}),
      ...(effectiveSettings.sobEmail ? { send_on_behalf_the_email: effectiveSettings.sobEmail } : {}),
      disable_email_notifications: Boolean(isSenderNotificationsDisabled),
      disable_recipient_email_notifications: false,
      disable_recipient_shipped_email: effectiveSettings.isRecipientShippedEmailDisabled,
      ...(email.isOn && email.emailTemplateId
        ? { email_template_id: email.emailTemplateId, email_subject: email.subject, email_message: email.body }
        : {}),
      receiver_reminder_interval: reminderInterval,
      ...(effectiveSettings.claimExpiration ? { claim_link_expiration: effectiveSettings.claimExpiration } : {}),
      disable_shipment_tracking: false,
      ...(!isContentDigital || effectiveShipping.isClaimBased
        ? mapRecipientsShipping(
            effectiveShipping.mode,
            effectiveShipping.isClaimBased,
            overrideAddress,
            claimAddressIds,
            isGroupShipmentEnabled,
            shippingOption,
          )
        : {}),
    };
  },
);
